import { create } from 'zustand';

import { normalizeVideoDoc, shotTimeline, type Decider, type RenderGroup, type TimelineEntry, type VideoDoc } from '@ai-studio/shared';

import { ApiError, api } from '../lib/api.js';
import { SAVE_DELAY_MS } from '../lib/doc-io.js';
import type { Pieces } from '../lib/video.js';
import { logEditor } from './console.js';
import { useWorkspace } from './workspace.js';
import { requireLocalGen } from './local-gen.js';

/**
 * The open Video Studio document (`*.video.json`). The storyboard is edited here (immutable doc, undo stack,
 * debounced save through PUT /video, which keeps the pipeline's records); the server reports the live state of
 * every piece (from the generation requests), the running render and the assembled file. Polled while the tab
 * is open.
 */

export const VIDEO_POLL_MS = 3000;
const HISTORY_LIMIT = 100;

export type VideoSelection = { kind: 'bible' } | { kind: 'shot'; id: string } | { kind: 'character'; id: string } | { kind: 'scene'; id: string } | { kind: 'audio' };
export type SaveState = 'saved' | 'pending' | 'saving' | 'error';

interface VideoResponse {
  doc: unknown;
  errors: string[];
  pieces: Pieces;
  timeline: { entries: TimelineEntry[]; total: number };
  outputDir: string;
  sequence: string;
  final: string | null;
  estimate: { animatic: number; final: number };
  director: { startedAt: string; groups: string[] } | null;
  decider: Decider;
}

interface VideoState {
  path: string | null;
  doc: VideoDoc | null;
  errors: string[];
  pieces: Pieces;
  outputDir: string;
  sequence: string;
  final: string | null;
  estimate: { animatic: number; final: number } | null;
  director: VideoResponse['director'];
  decider: Decider | null;
  version: string;
  loading: boolean;
  error: string | null;
  saveState: SaveState;
  selection: VideoSelection;
  /** Playhead (seconds on the video timeline). */
  time: number;
  playing: boolean;
  undoStack: VideoDoc[];
  redoStack: VideoDoc[];
  videos: string[];

  open: (path: string) => Promise<void>;
  close: () => void;
  refresh: () => Promise<void>;
  listVideos: () => Promise<void>;
  /** Apply a storyboard edit (undoable, saved after a short delay). */
  edit: (change: (doc: VideoDoc) => VideoDoc) => void;
  undo: () => void;
  redo: () => void;
  flush: () => Promise<void>;
  select: (selection: VideoSelection) => void;
  setTime: (time: number) => void;
  setPlaying: (playing: boolean) => void;
  create: (body: { name: string; mode?: string; targetLength?: number; width?: number; height?: number; fps?: number; prompt?: string; style?: string }) => Promise<string | null>;
  render: (body: { preset?: string; groups?: RenderGroup[]; scene?: string; shot?: string; keys?: string[] }) => Promise<boolean>;
  stop: () => Promise<void>;
  testVoice: (character: string, text?: string) => Promise<void>;
  writeSequence: () => Promise<string | null>;
  /** Timeline of the doc being edited. */
  timeline: () => { entries: TimelineEntry[]; total: number };
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let refreshSeq = 0;

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError || error instanceof Error ? error.message : fallback;
}

const pid = () => useWorkspace.getState().projectId;

export const useVideo = create<VideoState>((set, get) => ({
  path: null,
  doc: null,
  errors: [],
  pieces: {},
  outputDir: '',
  sequence: '',
  final: null,
  estimate: null,
  director: null,
  decider: null,
  version: '',
  loading: false,
  error: null,
  saveState: 'saved',
  selection: { kind: 'bible' },
  time: 0,
  playing: false,
  undoStack: [],
  redoStack: [],
  videos: [],

  open: async (path) => {
    if (get().path && get().path !== path) await get().flush();
    set({ path, doc: null, loading: true, error: null, selection: { kind: 'bible' }, time: 0, playing: false, undoStack: [], redoStack: [], saveState: 'saved' });
    await get().refresh();
    set({ loading: false });
  },

  close: () => {
    void get().flush();
    set({ path: null, doc: null, pieces: {}, errors: [], final: null, director: null, playing: false });
  },

  refresh: async () => {
    const projectId = pid();
    const path = get().path;
    if (projectId === null || !path) return;
    const seq = ++refreshSeq;
    try {
      const r = await api.get<VideoResponse>(`/projects/${projectId}/video?path=${encodeURIComponent(path)}`);
      if (seq !== refreshSeq || get().path !== path) return;
      // A local edit not saved yet wins over the copy on disk; the piece states always update.
      const editing = get().saveState !== 'saved';
      set({
        ...(editing ? {} : { doc: normalizeVideoDoc(r.doc), errors: r.errors }),
        pieces: r.pieces,
        outputDir: r.outputDir,
        sequence: r.sequence,
        final: r.final,
        estimate: r.estimate,
        director: r.director,
        decider: r.decider,
        version: String(Date.now()),
        error: null,
      });
    } catch (error) {
      if (seq !== refreshSeq) return;
      set({ error: message(error, 'Could not load the video.') });
    }
  },

  listVideos: async () => {
    const projectId = pid();
    if (projectId === null) return;
    try {
      const r = await api.get<{ videos: string[] }>(`/projects/${projectId}/videos`);
      set({ videos: r.videos });
    } catch {
      set({ videos: [] });
    }
  },

  edit: (change) => {
    const doc = get().doc;
    if (!doc) return;
    const next = change(doc);
    if (next === doc) return;
    set({ doc: next, undoStack: [...get().undoStack, doc].slice(-HISTORY_LIMIT), redoStack: [], saveState: 'pending' });
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => void get().flush(), SAVE_DELAY_MS);
  },

  undo: () => {
    const { undoStack, doc } = get();
    const prev = undoStack.at(-1);
    if (!prev || !doc) return;
    set({ doc: prev, undoStack: undoStack.slice(0, -1), redoStack: [...get().redoStack, doc], saveState: 'pending' });
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => void get().flush(), SAVE_DELAY_MS);
  },

  redo: () => {
    const { redoStack, doc } = get();
    const next = redoStack.at(-1);
    if (!next || !doc) return;
    set({ doc: next, redoStack: redoStack.slice(0, -1), undoStack: [...get().undoStack, doc], saveState: 'pending' });
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => void get().flush(), SAVE_DELAY_MS);
  },

  flush: async () => {
    if (saveTimer) {
      clearTimeout(saveTimer);
      saveTimer = null;
    }
    const { doc, path, saveState } = get();
    const projectId = pid();
    if (!doc || !path || projectId === null || saveState === 'saved') return;
    set({ saveState: 'saving' });
    try {
      const r = await api.put<{ doc: unknown; errors: string[] }>(`/projects/${projectId}/video`, { path, doc });
      // Keep edits made while saving; adopt the server's records otherwise.
      if (get().doc === doc) set({ doc: normalizeVideoDoc(r.doc), errors: r.errors, saveState: 'saved' });
      else set({ errors: r.errors, saveState: 'pending' });
    } catch (error) {
      set({ saveState: 'error' });
      logEditor('error', `Could not save ${path}: ${message(error, 'write failed')}`);
    }
  },

  select: (selection) => set({ selection }),
  setTime: (time) => set({ time: Math.max(0, time) }),
  setPlaying: (playing) => set({ playing }),

  create: async (body) => {
    const projectId = pid();
    if (projectId === null) return null;
    try {
      const r = await api.post<{ path: string }>(`/projects/${projectId}/video/create`, body);
      await get().listVideos();
      await get().open(r.path);
      return r.path;
    } catch (error) {
      logEditor('error', `Could not create the video: ${message(error, 'failed')}`);
      return null;
    }
  },

  render: async (body) => {
    const projectId = pid();
    const path = get().path;
    if (projectId === null || !path || !requireLocalGen()) return false;
    await get().flush();
    try {
      await api.post(`/projects/${projectId}/video/render`, { path, ...body });
      logEditor('info', `Video Studio: started ${body.preset ?? body.groups?.join(', ') ?? 'render'}${body.shot ? ` for ${body.shot}` : ''}.`);
      await get().refresh();
      return true;
    } catch (error) {
      const err = error as ApiError & { body?: { errors?: string[] } };
      logEditor('error', `Could not start the render: ${message(error, 'failed')}${err.body?.errors ? ` ${err.body.errors.slice(0, 3).join(' ')}` : ''}`);
      return false;
    }
  },

  stop: async () => {
    const projectId = pid();
    const path = get().path;
    if (projectId === null || !path) return;
    await api.post(`/projects/${projectId}/video/stop`, { path }).catch(() => undefined);
    await get().refresh();
  },

  testVoice: async (character, text) => {
    const projectId = pid();
    const path = get().path;
    if (projectId === null || !path || !requireLocalGen()) return;
    await get().flush();
    try {
      await api.post(`/projects/${projectId}/video/test-voice`, { path, character, ...(text ? { text } : {}) });
      await get().refresh();
    } catch (error) {
      logEditor('error', `Could not test the voice: ${message(error, 'failed')}`);
    }
  },

  writeSequence: async () => {
    const projectId = pid();
    const path = get().path;
    if (projectId === null || !path) return null;
    await get().flush();
    try {
      const r = await api.post<{ path: string }>(`/projects/${projectId}/video/sequence`, { path });
      return r.path;
    } catch (error) {
      logEditor('error', `Could not write the sequence: ${message(error, 'failed')}`);
      return null;
    }
  },

  timeline: () => {
    const doc = get().doc;
    return doc ? shotTimeline(doc) : { entries: [], total: 0 };
  },
}));

/* ------------------------------------------------------------------ */
/* Polling while the Video Studio is visible                           */

let pollers = 0;
let timer: ReturnType<typeof setTimeout> | null = null;

function schedule(): void {
  if (pollers === 0) return;
  timer = setTimeout(async () => {
    await useVideo.getState().refresh();
    schedule();
  }, VIDEO_POLL_MS);
}

export function acquireVideoPolling(): () => void {
  pollers++;
  if (pollers === 1) schedule();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    pollers--;
    if (pollers === 0 && timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
}
